import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { JumpToComponent } from '../../shared/components/jump-to/jump-to.component';
import { T1dComponent } from './t1d/t1d.component';
import { CalloutComponent } from '../../shared/components/callout/callout.component';
import { StagesComponent } from './stages/stages.component';
import { ImageWithTitleComponent } from '../../shared/components/image-with-title/image-with-title.component';
import { CtaBottomComponent } from '../../shared/components/cta-bottom/cta-bottom.component';
import { GlobalHeroComponent } from '../../shared/components/global-hero/global-hero.component';
import { appPaths } from '../../app.paths';
import { Observable } from 'rxjs';
import { ScrollSpyService } from '../../shared/services/scroll-spy.service';
import { CommonModule } from '@angular/common';
import { BetaCellsComponent } from './beta-cells/beta-cells.component';
import { ReviewBarComponent } from '../../shared/components/review-bar/review-bar.component';

@Component({
  selector: '[what-is-t1d]',
  imports: [
    CommonModule,
    GlobalHeroComponent,
    JumpToComponent,
    CalloutComponent,
    StagesComponent,
    ImageWithTitleComponent,
    CtaBottomComponent,
    BetaCellsComponent,
    ReviewBarComponent,
  ],
  templateUrl: './what-is-t1d.component.html',
  styleUrl: './what-is-t1d.component.scss',
})
export class WhatIsT1dComponent implements OnInit, OnDestroy {
  scrollSpyService = inject(ScrollSpyService);

  activeSection$!: Observable<string | null>;

  heroData = {
    backgroundImageDesktop: 'hero-stages',
    titleText: 'TYPE 1 DIABETES HAPPENS IN',
    bodyTextDesktopWidth: '',
    titleColorText: 'STAGES',
    bodyText:
      'Early stages of T1D come before symptoms <br class="d-none d-lg-inline" />and insulin dependence. The story starts <br class="d-none d-lg-inline" />with beta cells.',
    sillHouetteImage: 'hero-sillhouette-stages',
    altText: 'Actor portrayal',
    sillhouette: {
      mobile: {
        width: 293,
        top: 33,
        left: 47.58,
      },
      tablet: {
        width: 323,
        top: -1,
        left: 60,
      },
      desktop: {
        width: 599,
        height: 337,
        top: 6,
        left: 110,
      },
    },
    sillhouetteAlt: 'A father sitting on the couch with his daughter',
  };

  jumpToData = [
    {
      buttonLabel: 'Beta cells',
      buttonUrl: '/',
      buttonFragment: 'What-are-beta-cells',
    },
    {
      buttonLabel: 'Stages of type 1',
      buttonUrl: '/',
      buttonFragment: 'what-are-the-stages-of-type-1',
    },
    {
      buttonLabel: 'The risks are real',
      buttonUrl: '/',
      buttonFragment: 'The-risks-are-real',
    },
  ];

  calloutItems = [
    {
      icon: 'blood-cells',
      alt: 'A cluster of cells',
      title: 'The risks are real',
      text: `<strong>Diabetic ketoacidosis (DKA)</strong> is a dangerous complication of autoimmune T1D. It can happen when the blood becomes acidic (when ketones are present) because the body doesn’t have enough insulin to let blood sugar into your cells. DKA can cause coma, brain damage, and even death.`,
    },
    {
      icon: 'exclamation',
      alt: 'Exclamation points',
      title: `You don't have to be caught off guard!`,
      text: `A diagnosis of autoimmune type 1 diabetes often comes without warning and in a traumatic way like an emergency room visit. Early screening could help.`,
    },
    {
      icon: 'antibodies',
      alt: 'A magnifying glass looking at autoantibodies',
      title: `What’s the honeymoon phase?`,
      text: `After a stage 3 T1D diagnosis, some people go through a temporary “honeymoon phase.” During this time, the pancreas may still make small amounts of insulin, which can make blood sugar a little easier to manage and may reduce the amount of insulin needed. The phase doesn’t last forever — it might be weeks or even months — but it can give you and your care team time to adjust, learn about T1D, and prepare for long-term management with daily insulin.<br/><br/>Researchers are studying new ways to address the progression of T1D including during or after the honeymoon phase. Talk to your doctor about what the latest research may mean for you.`,
    },
  ];

  imageTitleData = {
    background: 'rgba(251, 234, 12, 0.06)',
    color: 'cyan',
    image: 'what-is-t1d/spot',
    alt: 'A young boy standing outdoors in the woods',
    title: 'The risks are real',
    text: [
      '<b>Diabetic ketoacidosis (DKA)</b> is a dangerous complication of autoimmune T1D. It can happen when the blood becomes acidic (when ketones are present) because the body doesn’t have enough insulin to let blood sugar into your cells. DKA can cause coma, brain damage, and even death.',
      `T1D diagnoses often come without warning, in potentially traumatic settings like an emergency room. <b>But you don't have to be caught off guard! Screen and spot T1D early.</b>`,
    ],
    cta: {
      title: 'Learn how to screen',
      url: appPaths.HOWTOSCREEN + '/',
      openInNewTab: false,
    },
  };

  ctaBottomData = {
    icon: 'doctors',
    alt: 'A care team',
    title: `Are you an HCP looking for<br class="d-none d-lg-inline" /> information for your patients? <br class="d-none d-lg-inline" />We've got resources for you!`,
    text: '',
    button: {
      buttonLabel: 'Discover HCP Resources',
      buttonUrl: appPaths.RESOURCE + '/',
      openInNewTab: false,
      buttonIcon: 'right-icon',
    },
  };

  ngOnInit(): void {
    const sectionIds = this.jumpToData.map((item) => item.buttonFragment);
    this.scrollSpyService.initObserver(sectionIds);

    this.activeSection$ = this.scrollSpyService.activeSection$;
  }

  ngOnDestroy(): void {
    this.scrollSpyService.disconnectObserver();
  }
}
